<template>
  <div>
    <mapa-leaflet-geoserver
      id-mapa="mapa-leaflet-geoserver"
      :centro-inicial="centroMapa"
      :bases="todasBases"
      :zoom-inicial="5"
      :filtro-cql="filtroCql"
      :filtro-ogc="filtroOgc"
      :obter-nome-layer="(base) => base.nome_tabela"
      :obter-rotulo-layer="(base) => base.descricao"
      :obter-filtro-layer="obterFiltroParaLayer"
      :ativar-layers-automaticamente="false"
      :layers-ativas-inicialmente="layersAtivasInicialmente"
      :aplicar-zoom-automatico="true"
      :zoom-maximo="zoomMaximoMapa"
    />
  </div>
</template>

<script>
  import MapaLeafletGeoserver from '@/components/general/mapas/MapaLeafletGeoserver.vue';
  import filtersMixins from '@/mixins/geoserver/filtersMixins';
  import dateFormatterMixins from '@/mixins/dateFormatterMixins';

  export default {
    name: 'MapaLocalizacaoGeografica',

    components: {
      MapaLeafletGeoserver
    },

    mixins: [filtersMixins, dateFormatterMixins],

    props: {
      layer: {
        type: String,
        default: null
      },
      filtrosAtivos: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        filtroCql: null,
        filtroOgc: null,
        basesFixas: [
          { nome_tabela: 'view_conj', descricao: 'CONJ' },
          { nome_tabela: 'view_qttv_regionais', descricao: 'REGIONAL' },
          { nome_tabela: 'view_municipios', descricao: 'MUNICIPIO' }
        ],
        baseQuantitativo: null,
        mapaNomeParaView: {
          postes: 'view_qttv_ponnot',
          transformadores: 'view_qttv_transformadores',
          transformadoresAt: 'view_qttv_transformadores_at',
          redeBt: 'view_qttv_rede_bt',
          redeMt: 'view_qttv_rede_mt',
          redeAt: 'view_qttv_rede_at',
          bancoCapacitor: 'view_qttv_banco_capacitor',
          ucBt: 'view_qttv_ucbt',
          ucMt: 'view_qttv_ucmt',
          ucAt: 'view_qttv_ucat',
          chaves: 'view_qttv_chaves',
          religador: 'view_qttv_religador',
          disjuntores: 'view_qttv_disjuntores',
          regulador: 'view_qttv_regulador',
          torres: 'view_qttv_torres',
          eqme: 'view_qttv_eqme'
        },
        layersBasesFixas: ['view_conj', 'view_qttv_regionais', 'view_municipios']
      };
    },

    computed: {
      idEmpresa() {
        return this.$store.getters.getCompanyId;
      },

      versaoBdgd() {
        return this.$store.getters.getUserSelectedCompanyBdgdVersion;
      },

      mesVersaoBdgd() {
        const mes = this.formatMonthToPicker(this.versaoBdgd.data_registro);
        return mes ? `${mes}-01` : null;
      },

      centroMapa() {
        const [longitude, latitude] = this.$store.getters.getCompanyCapitalCoordinates;
        return [latitude, longitude];
      },

      zoomMaximoMapa() {
        if (this.filtrosAtivos.conjuntos) {
          return 18;
        } else if (this.filtrosAtivos.municipios) {
          return 16;
        } else if (this.filtrosAtivos.regionais) {
          return 14;
        }
        return 12;
      },

      todasBases() {
        const bases = [...this.basesFixas];
        if (this.baseQuantitativo) {
          bases.push(this.baseQuantitativo);
        }
        return bases;
      },

      layersAtivasInicialmente() {
        const layersAtivas = [];

        if (this.baseQuantitativo) {
          layersAtivas.push(this.baseQuantitativo.nome_tabela);
        }

        if (this.filtrosAtivos.regionais && !this.filtrosAtivos.municipios) {
          layersAtivas.push('view_qttv_regionais');
        }

        if (this.filtrosAtivos.municipios) {
          layersAtivas.push('view_municipios');
        }

        if (this.filtrosAtivos.conjuntos) {
          layersAtivas.push('view_conj');
        }

        return layersAtivas;
      }
    },

    mounted() {
      this.buscarBasesDados();
      this.buscarFiltro();
    },

    methods: {
      buscarBasesDados() {
        if (this.layer && this.mapaNomeParaView[this.layer]) {
          const nomeView = this.mapaNomeParaView[this.layer];
          this.baseQuantitativo = {
            nome_tabela: nomeView,
            descricao: this.getDescricaoLayer(this.layer)
          };
        }
      },

      getDescricaoLayer(key) {
        const descricoes = {
          postes: 'Postes',
          transformadores: 'Transformadores MT',
          transformadoresAt: 'Transformadores AT',
          redeBt: 'Rede BT',
          redeMt: 'Rede MT',
          redeAt: 'Rede AT',
          bancoCapacitor: 'Banco Capacitor',
          ucBt: 'UCBT',
          ucMt: 'UCMT',
          ucAt: 'UCAT',
          chaves: 'Chaves',
          religador: 'Religador',
          disjuntores: 'Disjuntores',
          regulador: 'Regulador',
          torres: 'Torres',
          eqme: 'Equipamento Medidor'
        };
        return descricoes[key] || key;
      },

      obterFiltroParaLayer(base) {
        const nomeTabela = base.nome_tabela;

        // Filtro específico para REGIONAIS
        if (nomeTabela === 'view_qttv_regionais') {
          if (this.filtrosAtivos.regionais) {
            return this.isCqlFilter()
              ? `regional = '${this.filtrosAtivos.regionais}'`
              : `<PropertyIsEqualTo><PropertyName>regional</PropertyName><Literal>${this.filtrosAtivos.regionais}</Literal></PropertyIsEqualTo>`;
          }
          return null;
        }

        // Filtro específico para MUNICÍPIOS
        if (nomeTabela === 'view_municipios') {
          let filtroCql = "uf = 'AM'";
          let filtroOgc = '<PropertyIsEqualTo><PropertyName>uf</PropertyName><Literal>AM</Literal></PropertyIsEqualTo>';

          if (this.filtrosAtivos.municipios) {
            filtroCql += ` and cod_municipio_ibge = ${this.filtrosAtivos.municipios}`;
            filtroOgc = `<And>${filtroOgc}<PropertyIsEqualTo><PropertyName>cod_municipio_ibge</PropertyName><Literal>${this.filtrosAtivos.municipios}</Literal></PropertyIsEqualTo></And>`;
          }

          return this.isCqlFilter() ? filtroCql : filtroOgc;
        }

        // Filtro específico para CONJUNTOS
        if (nomeTabela === 'view_conj') {
          let filtroCql = this.getBasicCqlFilter(this.idEmpresa, this.mesVersaoBdgd, this.versaoBdgd.versao);
          let filtroOgc = this.getBasicOgcFilter(this.idEmpresa, this.mesVersaoBdgd, this.versaoBdgd.versao);

          if (this.filtrosAtivos.conjuntos) {
            filtroCql += ` and cod_id = ${this.filtrosAtivos.conjuntos}`;
            filtroOgc = `<And>${filtroOgc}<PropertyIsEqualTo><PropertyName>cod_id</PropertyName><Literal>${this.filtrosAtivos.conjuntos}</Literal></PropertyIsEqualTo></And>`;
          }

          return this.isCqlFilter() ? filtroCql : filtroOgc;
        }

        // Para outras layers (quantitativas), retorna o filtro apropriado
        return this.isCqlFilter() ? this.filtroCql : this.filtroOgc;
      },

      buscarFiltroCql() {
        let cqlFilter = this.getBasicCqlFilter(this.idEmpresa, this.mesVersaoBdgd, this.versaoBdgd.versao);

        if (this.filtrosAtivos.regionais) {
          cqlFilter += ` and (regional_a = '${this.filtrosAtivos.regionais}')`;
        }

        if (this.filtrosAtivos.municipios) {
          cqlFilter += ` and (cd_mun = ${this.filtrosAtivos.municipios})`;
        }

        if (this.filtrosAtivos.conjuntos) {
          cqlFilter += ` and (conj = '${this.filtrosAtivos.conjuntos}')`;
        }

        return cqlFilter;
      },

      buscarFiltroOgc() {
        let ogcFilter = this.getBasicOgcFilter(this.idEmpresa, this.mesVersaoBdgd, this.versaoBdgd.versao);

        let filtrosAdicionais = '';

        if (this.filtrosAtivos.regionais) {
          filtrosAdicionais += '<PropertyIsEqualTo>';
          filtrosAdicionais += '<PropertyName>regional_a</PropertyName>';
          filtrosAdicionais += `<Literal>${this.filtrosAtivos.regionais}</Literal>`;
          filtrosAdicionais += '</PropertyIsEqualTo>';
        }

        if (this.filtrosAtivos.municipios) {
          filtrosAdicionais += '<PropertyIsEqualTo>';
          filtrosAdicionais += '<PropertyName>cd_mun</PropertyName>';
          filtrosAdicionais += `<Literal>${this.filtrosAtivos.municipios}</Literal>`;
          filtrosAdicionais += '</PropertyIsEqualTo>';
        }

        if (this.filtrosAtivos.conjuntos) {
          filtrosAdicionais += '<PropertyIsEqualTo>';
          filtrosAdicionais += '<PropertyName>conj</PropertyName>';
          filtrosAdicionais += `<Literal>${this.filtrosAtivos.conjuntos}</Literal>`;
          filtrosAdicionais += '</PropertyIsEqualTo>';
        }

        if (filtrosAdicionais) {
          ogcFilter = `<And>${ogcFilter}${filtrosAdicionais}</And>`;
        }

        return ogcFilter;
      },

      buscarFiltro() {
        return this.isCqlFilter() ? (this.filtroCql = this.buscarFiltroCql()) : (this.filtroOgc = this.buscarFiltroOgc());
      }
    },

    watch: {
      layer() {
        this.buscarBasesDados();
      },

      filtrosAtivos: {
        deep: true,
        handler() {
          this.buscarFiltro();
        }
      }
    }
  };
</script>
